import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs/internal/Observable";
import { environment } from "../../../environments/environment";
import { GenericResponseDto } from "../models/generic-response.dto";
import { GetYearOverYearReportTilesCount } from "../models/dashboard/getYearOverYearReportTilesCount";
import { OrderBillingStatus } from "../models/dashboard/orderBillingStatus";
import { QR1ReportsDto } from "../models/dashboard/qr1ReportsDto";
import { QR2Reports } from "../models/dashboard/qr2Reports";
import { QR3ReportsResponse } from "../models/dashboard/qr3ReportsResponse";
import { UserSetting } from "../models/dashboard/userSetting";
import { CustomerAgingDetailReport } from "../models/reports/customerAgingDetailReport";
import { ReadyToBillDto } from "../models/reports/readyToBillDto";
import { YearToYearSummaryDashboardResponseGrid } from "../models/reports/yearToYearSummaryDashboardResponseGrid";
import { VerifyClientList } from "../models/client/verify/verifyClientList";
import { GetAllVendorsDto } from "../models/vendor/vendorListing/getAllVendors";
import { D365SynchroniserResponseDto } from "../models/common/syncData";

@Injectable({
  providedIn: "root",
})
export class DashboardService {
  constructor(private _http: HttpClient) {}

  private clientManagementBaseURL = environment.baseUrl + "/ClientManagement";
  private vendorManagementBaseURL = environment.baseUrl + "/Vendor";
  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";
  private userManagementBaseURL = environment.baseUrl + "/User";
  private d365SynchroniserBaseURL = environment.baseUrl + "/D365Synchroniser";
  private reportBaseURL = environment.baseUrl + "/Reports";

  getClientList(
    status: string
  ): Observable<GenericResponseDto<VerifyClientList[]>> {
    return this._http.get<GenericResponseDto<VerifyClientList[]>>(
      this.clientManagementBaseURL +
        `/GetAllClientsPaginated?ClientStatus=${status}&PAGING=${7}`
    );
  }

  getUnprotectedCustomersCount(): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.clientManagementBaseURL + `/GetUnprotectedCustomersCount`
    );
  }

  getPendingCharges(): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.reportBaseURL + `/GetPendingCharges`
    );
  }
  getInvoiceProposalsCount(): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.saleOrderManagementBaseURL + `/GetInvoiceProposalsCount`
    );
  }

  getCDICount(): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.reportBaseURL + `/GetCdiInvoicesCount`
    );
  }

  getInvoiceReport(invoiceId: string): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.reportBaseURL + `/GetInvoiceReport?invoiceId=${invoiceId}`
    );
  }

  getVendorTableRecord(): Observable<GenericResponseDto<GetAllVendorsDto[]>> {
    return this._http.get<GenericResponseDto<GetAllVendorsDto[]>>(
      this.vendorManagementBaseURL + `/GetAllVendors?PAGING=${7}`
    );
  }

  getCustomerAging(
    paging = 15
  ): Observable<GenericResponseDto<QR1ReportsDto[]>> {
    return this._http.get<GenericResponseDto<QR1ReportsDto[]>>(
      `${this.reportBaseURL}/GetQR1Reports?paging=${paging}`
    );
  }

  getArAgingDetailReport(
    paging = 15
  ): Observable<GenericResponseDto<CustomerAgingDetailReport[]>> {
    return this._http.get<GenericResponseDto<CustomerAgingDetailReport[]>>(
      this.reportBaseURL + `/GetCustomerAgingDetailReport?paging=${paging}`
    );
  }

  getOrdersWrittenthismonth(
    paging = 15
  ): Observable<GenericResponseDto<QR2Reports>> {
    return this._http.get<GenericResponseDto<QR2Reports>>(
      `${this.reportBaseURL}/GetQR2Reports?paging=${paging}`
    );
  }

  getOpenOrdersByInHandsDate(
    paging = 15
  ): Observable<GenericResponseDto<OrderBillingStatus[]>> {
    return this._http.get<GenericResponseDto<OrderBillingStatus[]>>(
      `${this.reportBaseURL}/GetOpenOrdersByInHandsDate?paging=${paging}`
    );
  }

  getOrdersBilledInvoiced(
    paging = 15
  ): Observable<GenericResponseDto<QR3ReportsResponse>> {
    return this._http.get<GenericResponseDto<QR3ReportsResponse>>(
      `${this.reportBaseURL}/GetQR3Reports?paging=${paging}`
    );
  }

  addSaleOrderHeaderData(
    payload: UserSetting
  ): Observable<GenericResponseDto<UserSetting>> {
    return this._http.post<GenericResponseDto<UserSetting>>(
      this.userManagementBaseURL + "/AddOrUpdateUserSettings",
      payload
    );
  }

  GetUserSettings(): Observable<GenericResponseDto<UserSetting>> {
    return this._http.get<GenericResponseDto<UserSetting>>(
      `${this.userManagementBaseURL}/GetUserSettings`
    );
  }

  getJNIDeletedRecordsQueueTrigger(): Observable<
    GenericResponseDto<D365SynchroniserResponseDto>
  > {
    return this._http.get<GenericResponseDto<D365SynchroniserResponseDto>>(
      `${this.d365SynchroniserBaseURL}/GetJNIDeletedRecordsQueueTrigger`
    );
  }

  getReadyToBillReport(
    paging = 15
  ): Observable<GenericResponseDto<ReadyToBillDto[]>> {
    return this._http.get<GenericResponseDto<ReadyToBillDto[]>>(
      this.reportBaseURL +
        `/GetOrderBillingStatusDashboardReport?paging=${paging}`
    );
  }

  getDisputedInvoicesCount(): Observable<GenericResponseDto<number>> {
    return this._http.get<GenericResponseDto<number>>(
      this.saleOrderManagementBaseURL + `/GetDisputedInvoicesCount`
    );
  }

  getYearOverYearSummaryCount(): Observable<
    GenericResponseDto<GetYearOverYearReportTilesCount>
  > {
    return this._http.get<GenericResponseDto<GetYearOverYearReportTilesCount>>(
      this.reportBaseURL + `/GetYearOverYearReportCount`
    );
  }

  getYearOverYearDashboardReport(): Observable<
    GenericResponseDto<YearToYearSummaryDashboardResponseGrid>
  > {
    return this._http.get<
      GenericResponseDto<YearToYearSummaryDashboardResponseGrid>
    >(this.reportBaseURL + `/GetYearToYearSummaryDashboardReport`);
  }
}
